<template>
  <div>
    <ul class="flex flex-col gap-4 md:gap-6">
      <li v-for="[venueTitle, seances] in seancesByVenue" :key="venueTitle">
        <UiTitle v-if="hasMoreVenues" severity="h5" class="mb-3 md:mb-2">{{ venueTitle }}</UiTitle>
        <ul class="scrollbar-hidden flex gap-2 overflow-x-scroll md:gap-4 lg:grid lg:grid-cols-5">
          <li
            v-for="seance in getVisibleSeances(seances)"
            :key="seance.id"
            class="min-w-2/3 md:min-w-1/3 lg:min-w-px"
          >
            <SeanceCard :value="seance" class="size-full" />
          </li>
          <li v-if="seances.length > maxVisibleSeances" class="md:min-w-1/3 lg:min-w-px">
            <UiCard
              tag="button"
              type="button"
              class="size-full px-5 text-center font-medium first-letter:lowercase md:text-left md:first-letter:capitalize"
              @click="emit('openTimetable', 'date')"
            >
              Показать еще
            </UiCard>
          </li>
        </ul>
      </li>
    </ul>
    <UiButtonLink
      v-if="seancesByVenueMap.size > MAX_VENUES_TO_SHOW"
      class="mt-6 flex items-center whitespace-nowrap text-xs xs:text-sm md:mt-7 md:text-base"
      @click="emit('openTimetable', 'venue')"
    >
      <UiIcon name="timetable" class="mr-1 size-3.5" />
      Посмотреть все доступные площадки и даты
    </UiButtonLink>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { type Seance, SeanceCard } from '@/5_entities/Seance'
import { useBreakpoint } from '@/6_shared/lib'
import type { TimetableFilterMode } from '@/6_shared/model'
import { UiButtonLink, UiCard, UiIcon, UiTitle } from '@/6_shared/ui'

type PropType = {
  value: Seance[]
  valueCount: number
}
type EmitType = {
  (e: 'openTimetable', mode: TimetableFilterMode): void
}

const props = defineProps<PropType>()
const emit = defineEmits<EmitType>()

const {
  lg: [isLgAndUp]
} = useBreakpoint()

type SeancesByVenue = Map<string, Seance[]>
const MAX_VENUES_TO_SHOW = 2
const MAX_SEANCES_DESKTOP = 4
const MAX_SEANCES_MOBILE = 4

/**
 * число отображаемых сеансов
 */
const maxVisibleSeances = computed(() => {
  if (isLgAndUp.value) return MAX_SEANCES_DESKTOP

  return MAX_SEANCES_MOBILE
})

const seancesByVenueMap = computed(() => {
  return props.value.reduce((acc, seance) => {
    const { venue } = seance

    if (!venue) return acc

    if (!acc.has(venue.title)) acc.set(venue.title, [] as Seance[])
    const venueSeances = acc.get(venue.title)!
    venueSeances.push(seance)

    return acc
  }, new Map() as SeancesByVenue)
})
const seancesByVenue = computed(() =>
  Array.from(seancesByVenueMap.value).filter((_, index) => index < MAX_VENUES_TO_SHOW)
)
const hasMoreVenues = computed(() => seancesByVenueMap.value.size >= MAX_VENUES_TO_SHOW)
const getVisibleSeances = (seances: Seance[]) =>
  seances.filter((_, index) => index < maxVisibleSeances.value)
</script>
